import { isBrowser } from '@/helpers/BrowserHelper';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import PaymentHelper, { KCP_PAY_METHOD_TYPE } from '@/helpers/PaymentHelper';
import { useUserStore } from '@/stores/UserStore';
import { KcpService } from '@/__generated__/CommonApi';
import { navigate } from 'gatsby';

const HiddenForm = styled.form`
  display: none;
`;

declare global {
  interface Window {
    KCP_Pay_Execute_Web: any;
    m_Completepayment: any;
  }
}

/* 이니시스로 원복하는 것을 대비해서 argument는 이전 데이터 유지 */
export interface KcpInitParams {
  pgProc: string; // 프로세스 'bill' | 'pay'
  siteCd: string;
  siteName: string;
  siteLogo: string;
  groupId?: string; // 빌키 발행용 그룹아이디
  currency: string;
  jsPluginUrl: string;
  payMethod: KCP_PAY_METHOD_TYPE['pc'];
  goodname: string;
  buyername: string;
  buyertel: string;
  buyeremail: string;
  price: number;
  oid: string;
  // custom
  completeUrl?: string;
  failUrl?: string;
  moduleYn?: string;
  gender?: string;
  missModuleCode?: string;
  missUserId?: string;
  solicitCode?: string;
  // kcp callback params
  kcpResCd?: string;
  kcpResMsg?: string;
  kcpEncInfo?: string;
  kcpEncData?: string;
  kcpTranCd?: string;
}

export interface KcpRequestParams {
  jsPluginUrl: string;
  site_cd: string;
  pay_method: KCP_PAY_METHOD_TYPE['pc'];
  site_name: string;
  currency: string;
  ordr_idxx: string;
  good_mny: number;
  good_name: string;
  good_cd: string;
  buyr_name: string;
  buyr_tel2: string;
  shop_user_id: string;
}

interface KcpHiddenFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  formData: KcpInitParams | null;
  donorId: string;
  isBilling?: boolean;
}

export interface KcpPcRef {
  init: (params: KcpInitParams) => void;
}

const useKcpPayment = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState<KcpInitParams | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const userStore = useUserStore();

  // 결제 승인 요청 (API 호출)
  const submitPayment = useCallback(
    async (data: any) => {
      try {
        if (formData == null) {
          return {
            resultCode: 'failed',
            resultMessage: '결제 요청 필수 정보가 누락되었습니다.',
          };
        }

        // 결제 승인 API 호출
        const validationKcpHub = await KcpService.reqKcpApprovalPayment({
          siteCd: formData?.siteCd,
          encData: data.enc_data,
          encInfo: data.enc_info,
          tranCd: data.tran_cd,
          ordrMony: formData?.price,
          ordrNo: formData?.oid,
          payType: formData?.payMethod,
        });

        if (validationKcpHub.resultCode === 'success') {
          return {
            resultCode: 'success',
            resultMessage: '후원이 완료되었습니다.',
          };
        } else {
          return {
            resultCode: 'failed',
            resultMessage: `[${validationKcpHub.resultCode}] ${
              validationKcpHub.resultMessage || '결제 인증에 실패했습니다.'
            }`,
          };
        }
      } catch (error) {
        console.error('결제 승인 오류:', error);
        alert('결제 처리 중 오류가 발생했습니다.');
        return {
          resultCode: 'failed',
          resultMessage: '결제 처리 중 오류가 발생했습니다.',
        };
      }
    },
    [formData],
  );

  // KCP 스크립트 로드
  const loadScript = useCallback((params: KcpInitParams) => {
    if (!params || !params.jsPluginUrl) {
      console.error('[kcp] js plugin url이 없습니다.');
      return;
    }

    const existingScript = document.getElementById('kcpPluginScript');
    if (existingScript) {
      console.log('[kcp] 스크립트가 이미 로드되어 있습니다.');
      setIsScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = params.jsPluginUrl;
    script.async = true;
    script.id = 'kcpPluginScript';

    script.onload = () => {
      setIsScriptLoaded(true);
    };

    script.onerror = () => {
      console.error('[kcp] KCP 스크립트 로드 실패');
      alert('결제 모듈 로드에 실패했습니다.');
    };

    document.body.appendChild(script);
  }, []);

  // FormOrJson 데이터 파싱
  const parseFormOrJson = (FormOrJson: any) => {
    let updatedData: any = {};

    if (typeof FormOrJson === 'object' && FormOrJson !== null) {
      if (typeof FormOrJson.res_cd === 'object') {
        updatedData = {
          res_cd: FormOrJson.elements['res_cd']?.value || '',
          res_msg: FormOrJson.elements['res_msg']?.value || '',
          enc_info: FormOrJson.elements['enc_info']?.value || '',
          enc_data: FormOrJson.elements['enc_data']?.value || '',
          tran_cd: FormOrJson.elements['tran_cd']?.value || '',
        };
      } else {
        updatedData = { ...FormOrJson };
      }
    } else if (typeof FormOrJson === 'string') {
      try {
        updatedData = JSON.parse(FormOrJson);
      } catch (e) {
        console.error('FormOrJson 파싱 실패:', e);
        throw new Error('결제 응답 데이터 처리 중 오류가 발생했습니다.');
      }
    }

    return updatedData;
  };

  // m_Completepayment 함수 등록
  useEffect(() => {
    window.m_Completepayment = async (
      FormOrJson: any,
      closeEvent: () => void,
    ) => {
      console.log('[kcp] m_Completepayment 호출됨');
      let isClose = true;
      try {
        const updatedData = parseFormOrJson(FormOrJson);
        const resCode = updatedData.res_cd;
        const resMsg = updatedData.res_msg;

        // 결제인증 결과 저장
        await KcpService.setKcpHubResultHistory({
          siteCd: formData?.siteCd,
          resCd: updatedData.res_cd,
          resMsg: updatedData.res_msg,
          ordrIdxx: formData?.oid,
          encData: updatedData.enc_data,
          encInfo: updatedData.enc_info,
          tranCd: updatedData.tran_cd,
        });

        if (resCode === '0000') {
          const paymentResult = await submitPayment(updatedData);
          if (paymentResult.resultCode === 'success') {
            isClose = false;
            PaymentHelper.onComplete(
              formData?.completeUrl || '/',
              formData?.oid || '',
            );
          } else {
            alert(paymentResult.resultMessage || '결제 승인에 실패했습니다.');
          }
        } else {
          alert(`[${resCode}] ${resMsg || '결제 인증에 실패했습니다.'}`);
        }
      } catch (error) {
        alert(
          error instanceof Error
            ? error.message
            : '결제 처리 중 오류가 발생했습니다.',
        );
      } finally {
        if (
          isClose === true &&
          closeEvent &&
          typeof closeEvent === 'function'
        ) {
          isClose = true;
          closeEvent();
        }
      }
    };

    return () => {
      if (window.m_Completepayment) {
        delete window.m_Completepayment;
      }
    };
  }, [submitPayment, formData]);

  // 결제 요청 실행
  const handlePayment = useCallback(() => {
    if (!formData) {
      console.error('formData가 없습니다.');
      return;
    }

    if (!window.KCP_Pay_Execute_Web) {
      console.error('KCP 결제 스크립트가 로드되지 않았습니다.');
      alert('결제 모듈이 로드되지 않았습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    try {
      if (formRef.current) {
        KcpService.setKcpHubRequestHistory({
          siteCd: formData.siteCd,
          paymentMethod: formData.payMethod,
          payUrl: '',
          approvalKey: '',
          retUrl: '',
          appUrl: '',
          ordrIdxx: formData.oid,
          goodMny: formData.price,
          goodName: formData.goodname,
          goodCd: formData.solicitCode,
          currency: formData.currency,
          shopUserId: userStore?.user?.donorId || '',
        });
        window.KCP_Pay_Execute_Web(formRef.current);
      } else {
        console.error('formRef.current가 없습니다.');
      }
    } catch (e) {
      console.log('[kcp] 결제창 실행 예외:', e);
    }
  }, [formData, userStore]);

  // 스크립트 로드 완료 후 결제 실행
  useEffect(() => {
    if (isScriptLoaded && formData) {
      const timer = setTimeout(() => {
        handlePayment();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isScriptLoaded, formData, handlePayment]);

  // KCP 결제 시작
  const start = useCallback(
    (params: KcpInitParams) => {
      const initFormData: KcpInitParams = {
        pgProc: params.pgProc,
        siteCd: params.siteCd,
        siteName: params.siteName,
        siteLogo: params.siteLogo,
        groupId: params.groupId,
        currency: 'WON',
        jsPluginUrl: params.jsPluginUrl,
        payMethod: params.pgProc === 'bill' ? 'AUTH:CARD' : params.payMethod,
        goodname: params.goodname || '',
        buyername: params.buyername || '',
        buyertel: params.buyertel || '',
        buyeremail: params.buyeremail || '',
        price: params.price || 0,
        oid: params.oid || '',
        completeUrl: params.completeUrl || '',
        failUrl: params.failUrl || '',
        moduleYn: params.moduleYn || 'N',
        gender: params.gender || '',
        missModuleCode: params.missModuleCode || '',
        missUserId: params.missUserId || '',
        solicitCode: params.solicitCode || '',
      };
      setFormData(initFormData);
      loadScript(params);
    },
    [loadScript],
  );
  return {
    formRef,
    formData,
    userStore,
    start,
  };
};

const KcpHiddenForm: React.FC<KcpHiddenFormProps> = ({
  formRef,
  formData,
  donorId,
  isBilling = false,
}) => {
  if (!isBrowser) {
    return null;
  }

  return createPortal(
    <HiddenForm
      ref={formRef}
      name="order_info"
      action={`${process.env.BACKEND_URL}/unicef/api/fo/common/payment/kcpPcForm`}
      method="post"
    >
      {/* 가맹점 정보 */}
      <input
        type="hidden"
        name="site_cd"
        value={formData?.siteCd || ''}
        readOnly
      />
      <input
        type="hidden"
        name="site_name"
        value={formData?.siteName || ''}
        readOnly
      />
      <input
        type="hidden"
        name="shop_name"
        value={formData?.siteName || ''}
        readOnly
      />
      <input
        type="hidden"
        name="site_logo"
        value={formData?.siteLogo || ''}
        readOnly
      />
      <input
        type="hidden"
        name="eng_flag"
        value={isBrowser && navigator.language.includes('ko') ? 'N' : 'Y'}
        readOnly
        title="영문 결제창 사용 여부"
      />
      {isBilling && (
        <input
          type="hidden"
          name="kcpgroup_id"
          value={formData?.groupId || ''}
          readOnly
        />
      )}
      {!isBilling && <input type="hidden" name="quotaopt" value="0" />}

      {/* 결제정보 */}
      <input
        type="hidden"
        name="shop_user_id"
        value={donorId}
        readOnly
        title="주문번호"
      />
      <input
        type="hidden"
        name="ordr_idxx"
        value={formData?.oid || ''}
        readOnly
        title="주문번호"
      />
      <input
        type="hidden"
        name="good_name"
        value={formData?.goodname || ''}
        readOnly
        title="상품명"
      />
      <input
        type="hidden"
        name="good_code"
        value={formData?.solicitCode || ''}
        readOnly
      />
      <input
        type="hidden"
        name="good_mny"
        value={formData?.price || 0}
        readOnly
        title="결제금액"
      />
      <input
        type="hidden"
        name="buyr_name"
        value={formData?.buyername || ''}
        readOnly
        title="구매자명"
      />
      <input
        type="hidden"
        name="buyr_tel2"
        value={formData?.buyertel || ''}
        readOnly
        title="구매자 연락처"
      />
      <input
        type="hidden"
        name="buyr_mail"
        value={formData?.buyeremail || ''}
        readOnly
        title="구매자 이메일"
      />

      {/* 결제 수단 정보 */}
      <input
        type="hidden"
        name="pay_method"
        value={formData?.payMethod || ''}
        readOnly
        title="결제수단"
      />
      {isBilling ? (
        <>
          <input
            type="hidden"
            name="card_cert_type"
            value="BATCH"
            readOnly
            title="인증방식 : 고정"
          />
          <input
            type="hidden"
            name="module_type"
            value="01"
            readOnly
            title="결제창 설정 정보 : 고정"
          />
          <input
            type="hidden"
            name="batch_soc"
            value="Y"
            readOnly
            title="NHN KCP 결제창 내에서 생년월일 입력 : 고정"
          />
          <input
            type="hidden"
            name="batch_cardno_return_yn"
            value="Y"
            readOnly
            title="배치키 발급 시 사용한 카드의 카드번호 리턴 여부"
          />
        </>
      ) : (
        <>
          <input
            type="hidden"
            name="currency"
            value={formData?.currency || 'WON'}
            readOnly
            title="결제 통화"
          />
        </>
      )}

      {/* Custom 필드 */}
      <input
        type="hidden"
        name="moduleYn"
        value={formData?.moduleYn || 'N'}
        readOnly
      />
      <input
        type="hidden"
        name="gender"
        value={formData?.gender || ''}
        readOnly
      />
      <input
        type="hidden"
        name="missModuleCode"
        value={formData?.missModuleCode || ''}
        readOnly
      />
      <input
        type="hidden"
        name="missUserId"
        value={formData?.missUserId || ''}
        readOnly
      />
      <input
        type="hidden"
        name="solicitCode"
        value={formData?.solicitCode || ''}
        readOnly
      />
      <input
        type="hidden"
        name="languageView"
        value={isBrowser && navigator.language.includes('ko') ? 'ko' : 'en'}
        readOnly
      />
    </HiddenForm>,
    document.body,
  );
};

const KcpPcForm = forwardRef<KcpPcRef>((props, ref) => {
  const { formRef, formData, userStore, start } = useKcpPayment();

  useImperativeHandle(ref, () => ({ init: start }), [start]);

  return (
    <KcpHiddenForm
      formRef={formRef}
      formData={formData}
      donorId={userStore?.user?.donorId || ''}
      isBilling={formData?.pgProc === 'bill'}
    />
  );
});
export default KcpPcForm;
